/**
 * Filtered Vendor Discovery Hook
 * 
 * A wrapper around useVendorDiscovery that automatically applies the current
 * user's organization business rules as hard filters on all vendor discovery queries.
 * 
 * Organization business rules are applied as exclusion filters - vendors who do not
 * meet the minimum criteria are completely hidden from search results.
 */

import { useAuth } from '@/lib/auth';
import { useVendorDiscovery, VendorSearchFilters, VendorSearchResult } from './useVendorDiscovery';
import { useOrganizationBusinessRules } from './useOrganizationBusinessRules';
import { BusinessRulesFilter, hasActiveBusinessRules } from '@/utils/businessRulesFilter';

/**
 * Hook that wraps useVendorDiscovery with automatic organization business rules filtering.
 * 
 * All vendor discovery functions automatically apply the user's organization's business rules
 * as hard filters. Vendors who don't meet the minimum verification level, rating,
 * completed jobs, or certification requirements will be excluded from results.
 * 
 * @returns Object with filtered vendor discovery functions
 * 
 * @example
 * ```tsx
 * function VendorSearch() {
 *   const { vendors, loading, searchVendors, businessRulesActive } = useFilteredVendorDiscovery();
 * 
 *   // This search will automatically exclude vendors who don't meet org business rules
 *   const handleSearch = async () => {
 *     await searchVendors({ location: 'California' });
 *   };
 * 
 *   return (
 *     <div>
 *       {businessRulesActive && (
 *         <Badge>Organization vendor filters active</Badge>
 *       )}
 *       {vendors.map(vendor => <VendorCard key={vendor.id} vendor={vendor} />)}
 *     </div>
 *   );
 * }
 * ```
 */
export function useFilteredVendorDiscovery() {
  const { user } = useAuth();
  const organizationId = user?.organization_id;
  const { rules, isLoading: rulesLoading } = useOrganizationBusinessRules(organizationId);
  const vendorDiscovery = useVendorDiscovery();

  // Convert organization rules to BusinessRulesFilter format
  const businessRulesFilter: BusinessRulesFilter | undefined = rules ? {
    minimum_vendor_verification_level: rules.minimum_vendor_verification_level as BusinessRulesFilter['minimum_vendor_verification_level'],
    minimum_vendor_rating: rules.minimum_vendor_rating,
    minimum_vendor_completed_jobs: rules.minimum_vendor_completed_jobs,
    required_certification_ids: rules.required_certification_ids,
  } : undefined;

  // Check if any business rules are actually active
  const businessRulesActive = hasActiveBusinessRules(businessRulesFilter);

  // Wrapped search function that applies business rules
  const searchVendors = async (
    filters: VendorSearchFilters = {},
    referenceLocation?: { latitude: number; longitude: number }
  ) => {
    return vendorDiscovery.searchVendors(filters, referenceLocation, businessRulesFilter);
  };

  // Wrapped featured vendors function
  const getFeaturedVendors = async (referenceLocation?: { latitude: number; longitude: number }) => {
    return vendorDiscovery.getFeaturedVendors(referenceLocation, businessRulesFilter);
  };

  // Wrapped category vendors function
  const getVendorsByCategory = async (category: string) => {
    return vendorDiscovery.getVendorsByCategory(category, businessRulesFilter);
  };

  return {
    // State
    vendors: vendorDiscovery.vendors,
    loading: vendorDiscovery.loading || rulesLoading,
    error: vendorDiscovery.error,
    
    // Business rules info
    businessRulesActive,
    businessRules: businessRulesFilter,
    
    // Wrapped functions (apply business rules automatically)
    searchVendors,
    getFeaturedVendors,
    getVendorsByCategory,
    
    // Pass-through functions (don't need business rules)
    getVendorProfile: vendorDiscovery.getVendorProfile,
    getRecentlyActiveVendors: vendorDiscovery.getRecentlyActiveVendors,
    clearResults: vendorDiscovery.clearResults,
    logProfileView: vendorDiscovery.logProfileView,
  };
}
